import { Doc } from '@dadohr/shared/domainTypes/document';
import { OktaApiIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/oktaApi';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../services/firebaseFunctions';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { removeOktaApiConfig, useOktaApiConfig } from './service';

const NAME = 'Okta API';

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const InitialSetupDialog = ({
  open,
  onClose,
  config
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  config: Doc<OktaApiIntegrationConfig>;
}) => {
  const { t } = useUnsafeTranslation();
  const [state, setState] = useState<{
    orgUrl: string;
    clientId: string;
    keyId: string;
    privateKey: string;
  }>({
    orgUrl: '',
    clientId: '',
    keyId: '',
    privateKey: ''
  });
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(
            'Integrate Dado and Okta API to automate adding employees to Okta Groups. For guidance on how to find the information requested in your Okta account, see our'
          )}{' '}
          <LinkExternal
            color="primary"
            href={
              'https://help.dadohr.com/article/117-okta-integrations#Setting-up-the-Okta-API-Integration-a3_GD'
            }
          >
            {t('help documentation.')}
          </LinkExternal>
        </Alert>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormRow>
          <CustomInputLabel label={'Org URL'} fullWidth>
            <TextField
              variant="outlined"
              value={state.orgUrl}
              onChange={(ev) =>
                setState((s) => ({ ...s, orgUrl: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow>
          <CustomInputLabel label={'Client ID'} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientId}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow>
          <CustomInputLabel label={'Key ID'} fullWidth>
            <TextField
              variant="outlined"
              value={state.keyId}
              onChange={(ev) =>
                setState((s) => ({ ...s, keyId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>

        <FormRow>
          <CustomInputLabel label={'Private Key'} fullWidth>
            <TextField
              variant="outlined"
              value={state.privateKey}
              multiline
              rows={12}
              onChange={(ev) =>
                setState((s) => ({ ...s, privateKey: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>
        </FormRow>
      </Content>

      <DialogActionsWithSlots
        right={
          <>
            <Button color="primary" onClick={onClose}>
              {t('Close')}
            </Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              onClick={async () => {
                setError(null);
                try {
                  await callFirebaseFunction(
                    'integrations_okta_api-verifyCredentialsAndStore',
                    {
                      orgId: config.data.orgId,
                      config: {
                        orgUrl: state.orgUrl,
                        clientId: state.clientId,
                        keyId: state.keyId,
                        privateKey: state.privateKey
                      }
                    }
                  );
                  onClose();
                } catch (e: any) {
                  // Surface the backend's HttpsError message (e.g. the Okta
                  // "assign an admin role" guidance) instead of silently failing.
                  setError(
                    e?.message ||
                      t(
                        'Could not verify the Okta credentials. Check the values and try again.'
                      )
                  );
                }
              }}
              disabled={
                !state.orgUrl ||
                !state.clientId ||
                !state.keyId ||
                !state.privateKey
              }
              pending={`${t('Saving')}...`}
            >
              {t('Save')}
            </ButtonWithPromise>
          </>
        }
      />
    </Dialog>
  );
};

const ExistingSetupDialog = ({
  open,
  onClose,
  org,
  config
}: {
  open: boolean;
  onClose: () => void;
  org: IOrganization;
  config: Doc<OktaApiIntegrationConfig>;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('Automate adding people to Okta Groups')}
        </Alert>

        <IntegrationData>
          <strong>{t('Org URL')}</strong>
          <div>{config.data.orgUrl}</div>
          <strong>{t('Client ID')}</strong>
          <div>{config.data.clientId}</div>
          <strong>{t('Key ID')}</strong>
          <div>{config.data.keyId}</div>
          <strong>{t('Private Key')}</strong>
          <div>{masked(config.data.privateKey)}</div>
        </IntegrationData>
      </Content>

      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={async () => {
              await removeOktaApiConfig(org.id);
            }}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

export const SetupDialog = ({
  config,
  onClose,
  onSuccessfulIntegration: onSucessfulIntegration, // can be ignored since this integration is not an import source
  open
}: IntegrationSetupDialogWithConfigProperties<OktaApiIntegrationConfig>) => {
  const setup = !!config?.data.privateKey;
  const { org } = useCurrentUser();

  return (
    <>
      {config && setup && (
        <ExistingSetupDialog
          org={org}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
      {config && !setup && (
        <InitialSetupDialog
          orgId={org.id}
          config={config}
          open={open}
          onClose={onClose}
        />
      )}
    </>
  );
};

export const OktaApiSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();

  const [config, , error] = useOktaApiConfig(org.id, userId);
  useErrorLogger('Okta Config error', error);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/okta.png"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
